/**
 * WorkspaceDocView — lazy-loaded multi-format document viewer (view-only).
 *
 * Wraps @cyntler/react-doc-viewer, which natively renders pdf, docx, doc,
 * xlsx, xls, pptx, csv, and images from a presigned URL. Code-split (see
 * AgentWorkspaceBrowser) so pdf.js / office renderers load ONLY when a user
 * actually opens a document — never in the base bundle.
 *
 * SigV4 presign gotcha: when a document has no ``fileType``, the viewer
 * MIME-sniffs the URL with a **HEAD** request. Our presigned URLs are signed
 * for **GET** (the verb is part of the SigV4 canonical request), so that HEAD
 * gets a 403 and the document silently fails to render — even though the same
 * URL downloads fine in a new tab. We avoid the sniff two ways:
 *   1. Pass an explicit ``fileType`` derived from the file extension — the
 *      viewer skips the HEAD entirely when ``fileType`` is set.
 *   2. Set ``prefetchMethod: 'GET'`` so any unknown/extensionless file that
 *      still needs sniffing does it with a GET the presign actually allows.
 */
import React from 'react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import '@cyntler/react-doc-viewer/dist/index.css';

// Extension → MIME the viewer's renderers key off. Covers the formats
// DocViewerRenderers supports; anything not listed falls back to the
// GET-based sniff (prefetchMethod below) rather than a 403-ing HEAD.
const EXT_MIME = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  tiff: 'image/tiff',
  csv: 'text/csv',
  htm: 'text/html',
  html: 'text/html',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

// Derive the MIME from the file name (ignoring any query string). The
// presigned URL carries ``book.pdf`` in the key, so the name is reliable.
export function fileTypeFromName(fileName) {
  const clean = String(fileName || '').split(/[?#]/)[0];
  const ext = clean.includes('.') ? clean.split('.').pop().toLowerCase() : '';
  return EXT_MIME[ext];
}

// OFFICE never leaves StepFlow (L160): the library's Office renderer hands the file to an EXTERNAL online viewer
// (view.officeapps.live.com) — it 404s on our short-lived links and would send the customer's document to a third
// party. Office files get a clear in-app message + Download here; the room's viewer renders them from the read text.
const OFFICE_MIME = new Set([EXT_MIME.doc, EXT_MIME.docx, EXT_MIME.xls, EXT_MIME.xlsx, EXT_MIME.ppt, EXT_MIME.pptx]);
const SAFE_RENDERERS = DocViewerRenderers.filter((r) => !(r.fileTypes || []).some((t) => OFFICE_MIME.has(t)));

export function isOfficeFile(fileName) {
  return OFFICE_MIME.has(fileTypeFromName(fileName));
}

export default function WorkspaceDocView({ url, fileName }) {
  if (!url) return null;
  const fileType = fileTypeFromName(fileName);
  if (OFFICE_MIME.has(fileType)) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center" data-testid="workspace-doc-office">
        <div className="text-[0.9375rem] font-medium text-gray-800">{fileName}</div>
        <div className="max-w-sm text-[0.8125rem] text-gray-500">
          Office files open in their own app — StepFlow doesn&apos;t send them to an outside viewer.
        </div>
        <a href={url} download={fileName} rel="noopener noreferrer" data-testid="workspace-doc-office-download"
          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700">
          Download
        </a>
      </div>
    );
  }
  return (
    <div className="h-full" data-testid="workspace-doc-view">
      <DocViewer
        documents={[{ uri: url, fileName, fileType }]}
        pluginRenderers={SAFE_RENDERERS}
        config={{
          header: { disableHeader: true, disableFileName: true },
          // Sign-safe fallback for any file we couldn't type from its name.
          prefetchMethod: 'GET',
        }}
        style={{ height: '100%' }}
      />
    </div>
  );
}
